import { TokenType } from './token-type.js';
import type { Token } from './token.js';
import { lookupKeyword } from './keywords.js';
import { normalizeIdentifierText } from './homoglyphs.js';
import { LexError } from './lex-error.js';
import type { Dialect } from '../dialect.js';

export interface LexResult {
  readonly tokens: Token[];
  readonly errors: LexError[];
}

function isLetter(ch: string): boolean {
  return /\p{L}/u.test(ch);
}

function isDigit(ch: string): boolean {
  return ch >= '0' && ch <= '9';
}

function isIdentifierStart(ch: string): boolean {
  return isLetter(ch) || ch === '_';
}

function isIdentifierPart(ch: string): boolean {
  return isLetter(ch) || isDigit(ch) || ch === '_';
}

/**
 * "Έξυπνα"/τυπογραφικά εισαγωγικά (π.χ. από αυτόματη διόρθωση σε Mac/Safari,
 * ή copy-paste από Word/Google Docs) — οπτικά αδιάκριτα από τα απλά, αλλά
 * διαφορετικοί χαρακτήρες Unicode. Χωρίς αυτή την ανοχή, ο lexer τα βλέπει
 * ως "μη αναγνωρίσιμο χαρακτήρα" και σπάει τη συμβολοσειρά στα δύο.
 */
const SINGLE_QUOTE_CHARS = new Set(["'", '‘', '’']); // ' ' '
const DOUBLE_QUOTE_CHARS = new Set(['"', '“', '”']); // " " "

function isQuoteChar(ch: string): boolean {
  return SINGLE_QUOTE_CHARS.has(ch) || DOUBLE_QUOTE_CHARS.has(ch);
}

/**
 * Λεξικός αναλυτής (Lexer) για ΓΛΩΣΣΑ / Ψευδογλώσσα.
 *
 * Μετατρέπει πηγαίο κώδικα σε ακολουθία tokens. Δεν σταματά στο πρώτο
 * σφάλμα· συλλέγει όσα λάθη βρει (μη αναγνωρίσιμοι χαρακτήρες, μη
 * τερματισμένες συμβολοσειρές) και συνεχίζει, ώστε ένα εκπαιδευτικό
 * περιβάλλον να μπορεί να τα δείξει όλα μαζί στον μαθητή.
 */
export class Lexer {
  private readonly source: string;
  private readonly dialect: Dialect | undefined;
  private pos = 0;
  private line = 1;
  private column = 1;
  private readonly tokens: Token[] = [];
  private readonly errors: LexError[] = [];

  constructor(source: string, dialect?: Dialect) {
    this.source = source;
    this.dialect = dialect;
  }

  tokenize(): LexResult {
    while (!this.isAtEnd()) {
      this.scanToken();
    }
    this.tokens.push({ type: TokenType.EOF, lexeme: '', line: this.line, column: this.column });
    return { tokens: this.tokens, errors: this.errors };
  }

  private isAtEnd(): boolean {
    return this.pos >= this.source.length;
  }

  private peek(offset = 0): string {
    return this.source.charAt(this.pos + offset);
  }

  private advance(): string {
    const ch = this.source.charAt(this.pos);
    this.pos++;
    if (ch === '\n') {
      this.line++;
      this.column = 1;
    } else {
      this.column++;
    }
    return ch;
  }

  private match(expected: string): boolean {
    if (this.peek() !== expected) return false;
    this.advance();
    return true;
  }

  private addToken(
    type: TokenType,
    lexeme: string,
    startLine: number,
    startColumn: number,
    value?: string | number,
  ): void {
    const token: Token =
      value === undefined
        ? { type, lexeme, line: startLine, column: startColumn }
        : { type, lexeme, value, line: startLine, column: startColumn };
    this.tokens.push(token);
  }

  private addError(message: string, line: number, column: number): void {
    this.errors.push(new LexError(message, line, column));
  }

  private scanToken(): void {
    const startLine = this.line;
    const startColumn = this.column;
    const ch = this.advance();

    switch (ch) {
      case ' ':
      case '\t':
      case '\r':
        return;
      case '\n':
        this.addToken(TokenType.NEWLINE, '\n', startLine, startColumn);
        return;
      case '!':
        this.scanComment(startLine, startColumn);
        return;
      case '&':
        this.scanLineContinuation(startLine, startColumn);
        return;
      case '+':
        this.addToken(TokenType.PLUS, '+', startLine, startColumn);
        return;
      case '-':
        this.addToken(TokenType.MINUS, '-', startLine, startColumn);
        return;
      case '*':
        this.addToken(TokenType.STAR, '*', startLine, startColumn);
        return;
      case '/':
        // Προσοχή: το "//" ΔΕΝ είναι σχόλιο εδώ — στο "Δεδομένα // Α, Β //"
        // είναι πραγματική εντολή (ανάκτηση δεδομένων Α, Β). Ο lexer δεν
        // μαντεύει τη γραμματική· αφήνει δύο διαδοχικά SLASH και το μοτίβο
        // το αναγνωρίζει ο Parser (Φάση 2).
        this.addToken(TokenType.SLASH, '/', startLine, startColumn);
        return;
      case '^':
        this.addToken(TokenType.CARET, '^', startLine, startColumn);
        return;
      case '(':
        this.addToken(TokenType.LPAREN, '(', startLine, startColumn);
        return;
      case ')':
        this.addToken(TokenType.RPAREN, ')', startLine, startColumn);
        return;
      case '[':
        this.addToken(TokenType.LBRACKET, '[', startLine, startColumn);
        return;
      case ']':
        this.addToken(TokenType.RBRACKET, ']', startLine, startColumn);
        return;
      case ',':
        this.addToken(TokenType.COMMA, ',', startLine, startColumn);
        return;
      case ':':
        this.addToken(TokenType.COLON, ':', startLine, startColumn);
        return;
      case '=':
        this.addToken(TokenType.EQUAL, '=', startLine, startColumn);
        return;
      case '<':
        if (this.match('=')) {
          this.addToken(TokenType.LESS_EQUAL, '<=', startLine, startColumn);
        } else if (this.match('>')) {
          this.addToken(TokenType.NOT_EQUAL, '<>', startLine, startColumn);
        } else if (this.match('-')) {
          // ASCII εναλλακτική του ←. Λόγω maximal munch, "α<-5" γίνεται
          // "α ← 5" και όχι "α < -5" — γι' αυτό συνιστάται κενό γύρω από
          // το "<" όταν πρόκειται για σύγκριση με αρνητικό αριθμό.
          // ΔΕΝ αναγνωρίζει "<--" σαν τρίτο σύμβολο του ΙΔΙΟΥ τελεστή πια
          // (παλιότερα εναλλακτική γραφή για το περιβάλλον ΓΛΩΣΣΟΜΑΘΕΙΑ) —
          // η δεύτερη παύλα μένει ΞΕΧΩΡΙΣΤΟ token (μοναδιαίο πλην), ώστε
          // "χ<--5" να σημαίνει ΠΑΝΤΑ "χ ← -5" (αρνητικό 5) και όχι σιωπηλά
          // "χ ← 5" (θετικό) — πραγματική παγίδα που εντόπισε χρήστης: η
          // παλιά γραφή "κατάπινε" την τρίτη παύλα μέσα στο ίδιο το βέλος.
          this.addToken(TokenType.ASSIGN, '<-', startLine, startColumn);
        } else {
          this.addToken(TokenType.LESS, '<', startLine, startColumn);
        }
        return;
      case '>':
        if (this.match('=')) {
          this.addToken(TokenType.GREATER_EQUAL, '>=', startLine, startColumn);
        } else {
          this.addToken(TokenType.GREATER, '>', startLine, startColumn);
        }
        return;
      case '←':
        this.addToken(TokenType.ASSIGN, '←', startLine, startColumn);
        return;
      case '≠':
        this.addToken(TokenType.NOT_EQUAL, '≠', startLine, startColumn);
        return;
      case '.':
        // Μόνο σαν μέρος του ".." (διάστημα ΠΕΡΙΠΤΩΣΗΣ, π.χ. "10..15") — ένα
        // μεμονωμένο "." δεν σημαίνει τίποτα εδώ (δεκαδικοί αριθμοί
        // αναγνωρίζονται ήδη μέσα στο scanNumber, πριν φτάσουμε ποτέ εδώ).
        if (this.match('.')) {
          this.addToken(TokenType.DOT_DOT, '..', startLine, startColumn);
        } else {
          this.addError(`Μη αναγνωρίσιμος χαρακτήρας: '.'`, startLine, startColumn);
        }
        return;
      default:
        break;
    }

    if (isQuoteChar(ch)) {
      this.scanString(ch, startLine, startColumn);
      return;
    }

    if (isDigit(ch)) {
      this.scanNumber(ch, startLine, startColumn);
      return;
    }

    if (isIdentifierStart(ch)) {
      this.scanIdentifierOrKeyword(ch, startLine, startColumn);
      return;
    }

    this.addError(`Μη αναγνωρίσιμος χαρακτήρας: '${ch}'`, startLine, startColumn);
  }

  private scanComment(startLine: number, startColumn: number): void {
    let text = '!';
    while (!this.isAtEnd() && this.peek() !== '\n') {
      text += this.advance();
    }
    this.addToken(TokenType.COMMENT, text, startLine, startColumn, text);
  }

  /**
   * "&" στο τέλος γραμμής = συνέχιση της εντολής στην επόμενη γραμμή (π.χ.
   * μια μεγάλη ΓΡΑΨΕ που δεν χωράει σε μία γραμμή). Δεν παράγεται κανένα
   * token — ούτε για το ίδιο το "&" ούτε για το newline που καταναλώνει —
   * οπότε η επόμενη γραμμή φτάνει στον Parser σαν να ήταν συνέχεια της
   * ίδιας λογικής γραμμής, χωρίς καμία αλλαγή στη γραμματική/Parser.
   */
  private scanLineContinuation(startLine: number, startColumn: number): void {
    while (!this.isAtEnd() && (this.peek() === ' ' || this.peek() === '\t' || this.peek() === '\r')) {
      this.advance();
    }
    if (this.isAtEnd()) return; // "&" στο τέλος του αρχείου — αγνοείται σιωπηλά
    if (this.peek() === '\n') {
      this.advance();
      return;
    }
    this.addError(
      "Το '&' (συνέχιση εντολής) πρέπει να είναι το τελευταίο στοιχείο της γραμμής",
      startLine,
      startColumn,
    );
  }

  /**
   * Δέχεται είτε 'μονά' είτε "διπλά" εισαγωγικά, απλά ή "έξυπνα"/τυπογραφικά
   * (‘…’ ή "…") — το closing πρέπει να ανήκει στην ίδια οικογένεια
   * (μονά/διπλά) με το opening, όχι απαραίτητα τον ίδιο ακριβώς χαρακτήρα
   * (π.χ. το αυτόματο κλείσιμο μιας "έξυπνης" αγκύλης δίνει διαφορετικό
   * χαρακτήρα Unicode από αυτόν που πληκτρολογήθηκε στο άνοιγμα).
   */
  private scanString(openingQuote: string, startLine: number, startColumn: number): void {
    const family = SINGLE_QUOTE_CHARS.has(openingQuote) ? SINGLE_QUOTE_CHARS : DOUBLE_QUOTE_CHARS;
    let value = '';
    while (!this.isAtEnd() && this.peek() !== '\n' && !family.has(this.peek())) {
      value += this.advance();
    }

    if (this.isAtEnd() || this.peek() === '\n') {
      this.addError(`Μη τερματισμένη συμβολοσειρά (λείπει το κλείσιμο ${openingQuote})`, startLine, startColumn);
      return;
    }

    const closingQuote = this.advance(); // καταναλώνει το κλείσιμο εισαγωγικό
    this.addToken(TokenType.STRING, `${openingQuote}${value}${closingQuote}`, startLine, startColumn, value);
  }

  private scanNumber(firstDigit: string, startLine: number, startColumn: number): void {
    let text = firstDigit;
    while (isDigit(this.peek())) {
      text += this.advance();
    }

    if (this.peek() === '.' && isDigit(this.peek(1))) {
      text += this.advance(); // '.'
      while (isDigit(this.peek())) {
        text += this.advance();
      }
    }

    this.addToken(TokenType.NUMBER, text, startLine, startColumn, Number(text));
  }

  private scanIdentifierOrKeyword(firstChar: string, startLine: number, startColumn: number): void {
    let text = firstChar;
    while (isIdentifierPart(this.peek())) {
      text += this.advance();
    }

    // Πρώτα δοκιμάζουμε το ΑΝΕΠΕΞΕΡΓΑΣΤΟ κείμενο (έτσι το DIV/MOD -- σκόπιμα
    // ολόκληρα λατινικά, ΟΧΙ ελληνική λέξη -- αναγνωρίζονται σωστά χωρίς να
    // "σπάσουν" από την κανονικοποίηση παρακάτω). Μόνο αν αυτό αποτύχει,
    // δοκιμάζουμε το κείμενο με κανονικοποιημένα λατινικά ομοιόμορφα γράμματα
    // (π.χ. λατινικό "KAI" -> "ΚΑΙ") — μια λέξη-κλειδί γραμμένη με ακούσια
    // ανάμειξη Ελληνικών/Λατινικών χαρακτήρων (οπτικά πανομοιότυπη σε κάθε
    // γραμματοσειρά) πρέπει να αναγνωρίζεται κανονικά, αντί να γίνεται
    // σιωπηλά IDENTIFIER και να σκάει ασαφές συντακτικό σφάλμα αλλού.
    const keyword = lookupKeyword(text, this.dialect) ?? lookupKeyword(normalizeIdentifierText(text), this.dialect);
    if (keyword !== undefined) {
      this.addToken(TokenType.KEYWORD, text, startLine, startColumn, keyword);
    } else {
      this.addToken(TokenType.IDENTIFIER, text, startLine, startColumn, normalizeIdentifierText(text));
    }
  }
}

/**
 * Βοηθητική συνάρτηση για γρήγορη χρήση χωρίς ρητή δημιουργία instance.
 * Το `dialect` είναι προαιρετικό — χωρίς αυτό αναγνωρίζεται το πλήρες
 * υπερσύνολο λέξεων-κλειδιών και των δύο διαλέκτων (παλιά συμπεριφορά,
 * χρήσιμη όταν η διάλεκτος δεν έχει σημασία, π.χ. εργαλεία εξέτασης
 * tokens). Δίνοντας τη διάλεκτο, λέξεις αποκλειστικές της ΑΛΛΗΣ διαλέκτου
 * (π.χ. "Δεδομένα" σε πρόγραμμα ΓΛΩΣΣΑΣ) δεν δεσμεύονται — μπορούν να
 * χρησιμοποιηθούν ελεύθερα ως αναγνωριστικά.
 */
export function tokenize(source: string, dialect?: Dialect): LexResult {
  return new Lexer(source, dialect).tokenize();
}
